import { memo, useCallback, useRef } from 'react';
import setPng from '@knowledge/assets/set.png';
import usnetPng from '@knowledge/assets/unset.png';
import { Tooltip, Modal, message, CheckboxProps, Checkbox } from 'antd';
import { useTranslate } from '@tolgee/react';
import { useMutation } from '@tanstack/react-query';
import { setKnowledgeDefault } from '@knowledge/knowledges/singleClassNew/api';
import { QuestionCircleOutlined } from '@ant-design/icons';

interface ConfigDefaultIconProps {
  isDefaultValue: boolean;
  knowledgeId?: number;
  knowledgeValueId?: string;
  onSuccess?: () => void;
  hasDefaultValue: boolean;
  onCiteModeClick?: () => void;
}

export const useSetCheck: <T extends (...args: any[]) => any>(
  fn: T,
  when?: () => boolean,
  options?: {
    isChange?: boolean;
    isCancel?: boolean;
  },
) => (...args: Parameters<T>) => ReturnType<T> = (fn, when = () => true, options = {}) => {
  const hasSet = useRef(localStorage.getItem('schassetdv') === 'true');
  const { t } = useTranslate();
  return useCallback(
    (...args) => {
      if (!when?.()) {
        return fn(...args);
      } else {
        if (options.isChange) {
          Modal.confirm({
            title: t('确认更改默认值？'),
            content: t('确认后，后续新加入的SKU会自动使用该默认值回复。'),
            onOk: () => {
              return fn(...args);
            },
          });
        } else if (options.isCancel) {
          Modal.confirm({
            title: t('取消默认值'),
            content: t('取消知识设置默认值后，后续新加入的SKU不再使用该知识值回复。'),
            onOk: () => {
              return fn(...args);
            },
          });
        } else if (!hasSet.current) {
          hasSet.current = true;
          localStorage.setItem('schassetdv', 'true');
          Modal.confirm({
            title: t('设为默认值'),
            content: t('知识设置默认值后，后续新加入的SKU会自动使用该知识的默认值回复。'),
            onOk: () => {
              return fn(...args);
            },
          });
        } else {
          return fn(...args);
        }
      }
    },
    [t, fn, when, options],
  );
};
export const ConfigDefaultIcon = memo((props: ConfigDefaultIconProps) => {
  const {
    isDefaultValue,
    knowledgeValueId,
    knowledgeId,
    onSuccess,
    hasDefaultValue,
    onCiteModeClick,
  } = props;
  const { t } = useTranslate();
  const { mutateAsync } = useMutation({
    mutationFn: setKnowledgeDefault,
    onSuccess: () => {
      onSuccess && onSuccess();
    },
    onError: () => {
      message.error(t('translation.操作失败，请稍后再试'));
    },
  });
  const checkFn = useSetCheck(
    () => {
      return mutateAsync({
        spuKnowledgeDefinitionId: knowledgeId!,
        defaultValueId: knowledgeValueId!,
      });
    },
    undefined,
    {
      isChange: !isDefaultValue && hasDefaultValue,
      isCancel: isDefaultValue && !hasDefaultValue,
    },
  );

  return (
    <Tooltip title={isDefaultValue ? t('取消默认值') : t('设为默认值')}>
      <div
        className="my-[4px] cursor-pointer"
        onClick={() => {
          if (onCiteModeClick) {
            onCiteModeClick();
          } else {
            checkFn();
          }
        }}
      >
        {isDefaultValue ? (
          <img width={18} src={setPng} alt="set" />
        ) : (
          <img width={18} src={usnetPng} alt="usnet" />
        )}
      </div>
    </Tooltip>
  );
});

ConfigDefaultIcon.displayName = 'ConfigDefaultIcon';

interface ConfigDefaultCheckboxProps extends CheckboxProps {}
export const ConfigDefaultCheckbox = memo((props: ConfigDefaultCheckboxProps) => {
  const { t } = useTranslate();
  return (
    <Checkbox {...props}>
      <div className="text-[#515359]">
        {t('设为默认值')}
        <Tooltip
          title={t(
            '知识设置默认值后，后续新加入的SKU会自动使用该知识的默认值回复。同个知识下仅能设置一个默认值。',
          )}
        >
          <QuestionCircleOutlined style={{ color: '#515359', marginLeft: '2px' }} />
        </Tooltip>
      </div>
    </Checkbox>
  );
});

ConfigDefaultCheckbox.displayName = 'ConfigDefaultCheckbox';
